import { addCallback } from '../../../lib/teact/teactn';

import type { IThemeSettings, LangCode } from '../../../types';
import type { ActionReturnType, GlobalState } from '../../types';

import { DEBUG } from '../../../config';
import { requestMutation } from '../../../lib/fasterdom/fasterdom';
import {
  CLOUD_THEME_FORMAT,
  findCloudThemeById,
  resolveCloudThemeColorOverrides,
  resolveCloudThemeSettings,
} from '../../../util/cloudThemes';
import { disableDebugConsole, initDebugConsole, logDebugMessage } from '../../../util/debugConsole';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { oldSetLanguage, setTimeFormat } from '../../../util/oldLangProvider';
import { applyPerformanceSettings } from '../../../util/perfomanceSettings';
import switchTheme, { setThemeColorOverrides } from '../../../util/switchTheme';
import { updatePeerColors } from '../../../util/theme';
import { buildThemeWallpaperDebugSnapshot } from '../../../util/themeWallpaper';
import { IS_IOS } from '../../../util/windowEnvironment';
import { callApi, setShouldEnableDebugLog } from '../../../api/gramjs';
import {
  addActionHandler, getActions, getGlobal, setGlobal,
} from '../../index';
import { replaceSettings, replaceThemeSettings } from '../../reducers';
import { updateTabState } from '../../reducers/tabs';
import { selectCanAnimateInterface } from '../../selectors';

let prevGlobal: GlobalState | undefined;

addCallback((global: GlobalState) => {
  // eslint-disable-next-line eslint-multitab-tt/no-getactions-in-actions
  const { updatePageTitle, updateShouldDebugExportedSenders, updateShouldEnableDebugLog } = getActions();

  const oldGlobal = prevGlobal;
  prevGlobal = global;

  if (!oldGlobal) return;

  const settings = global.settings.byKey;
  const prevSettings = oldGlobal.settings.byKey;
  const performance = global.settings.performance;
  const prevPerformance = oldGlobal.settings.performance;
  const peerColors = global.peerColors;
  const prevPeerColors = oldGlobal.peerColors;
  const hasThemeModeChanged = settings.theme !== prevSettings.theme;
  const hasCloudThemeChanged = settings.cloudThemeId !== prevSettings.cloudThemeId;
  const hasCloudThemeListChanged = global.settings.cloudThemes.hash !== oldGlobal.settings.cloudThemes.hash;

  if (peerColors && peerColors !== prevPeerColors) {
    updatePeerColors(peerColors.general);
  }

  if (performance !== prevPerformance) {
    requestMutation(() => {
      applyPerformanceSettings(performance);
    });
  }

  if (hasThemeModeChanged || hasCloudThemeChanged || hasCloudThemeListChanged) {
    const colorOverrides = resolveCloudThemeColorOverrides(
      global.settings.cloudThemes.themes,
      settings.cloudThemeId,
    );
    setThemeColorOverrides(colorOverrides);

    const withAnimation = hasThemeModeChanged && (document.hasFocus() ? selectCanAnimateInterface(global) : false);
    switchTheme(settings.theme, withAnimation, !hasThemeModeChanged);
  }

  if (hasThemeModeChanged && settings.cloudThemeId) {
    const selectedCloudTheme = findCloudThemeById(global.settings.cloudThemes.themes, settings.cloudThemeId);

    if (selectedCloudTheme) {
      void callApi('installTheme', {
        themeId: selectedCloudTheme.id,
        accessHash: selectedCloudTheme.accessHash,
        isDark: settings.theme === 'dark',
        format: CLOUD_THEME_FORMAT,
      });

      const selectedSetting = resolveCloudThemeSettings(selectedCloudTheme, settings.theme);
      if (selectedSetting?.wallpaper?.slug) {
        void callApi('installThemeWallpaper', {
          slug: selectedSetting.wallpaper.slug,
          settings: selectedSetting.wallpaperSettings || selectedSetting.wallpaper.settings,
        });
      }
    }
  }

  if (settings.language !== prevSettings.language) {
    oldSetLanguage(settings.language as LangCode);
  }

  if (settings.timeFormat !== prevSettings.timeFormat) {
    setTimeFormat(settings.timeFormat);
  }

  if (settings.messageTextSize !== prevSettings.messageTextSize) {
    document.documentElement.style.setProperty(
      '--composer-text-size', `${Math.max(settings.messageTextSize, IS_IOS ? 16 : 15)}px`,
    );
    document.documentElement.style.setProperty('--message-meta-height',
      `${Math.floor(settings.messageTextSize * 1.3125)}px`);
    document.documentElement.style.setProperty('--message-text-size', `${settings.messageTextSize}px`);
    document.documentElement.setAttribute('data-message-text-size', settings.messageTextSize.toString());
  }

  if (settings.canDisplayChatInTitle !== prevSettings.canDisplayChatInTitle) {
    updatePageTitle();
  }

  if (settings.shouldForceHttpTransport !== prevSettings.shouldForceHttpTransport) {
    callApi('setForceHttpTransport', Boolean(settings.shouldForceHttpTransport));
  }

  if (settings.shouldAllowHttpTransport !== prevSettings.shouldAllowHttpTransport) {
    callApi('setAllowHttpTransport', Boolean(settings.shouldAllowHttpTransport));
    if (!settings.shouldAllowHttpTransport && settings.shouldForceHttpTransport) {
      global = getGlobal();
      global = {
        ...global,
        settings: {
          ...global.settings,
          byKey: {
            ...global.settings.byKey,
            shouldForceHttpTransport: false,
          },
        },
      };
      setGlobal(global);
    }
  }

  if (settings.shouldDebugExportedSenders !== prevSettings.shouldDebugExportedSenders) {
    updateShouldDebugExportedSenders();
  }

  if (settings.shouldCollectDebugLogs !== prevSettings.shouldCollectDebugLogs) {
    updateShouldEnableDebugLog();
  }
});

addActionHandler('updateShouldEnableDebugLog', (global): ActionReturnType => {
  const { settings } = global;

  if (settings.byKey.shouldCollectDebugLogs) {
    setShouldEnableDebugLog(true);
    initDebugConsole();
  } else {
    setShouldEnableDebugLog(false);
    disableDebugConsole();
  }
});

addActionHandler('updateShouldDebugExportedSenders', (global): ActionReturnType => {
  const { settings } = global;
  callApi('setShouldDebugExportedSenders', Boolean(settings.byKey.shouldDebugExportedSenders));
});

addActionHandler('setSettingOption', (global, actions, payload): ActionReturnType => {
  return replaceSettings(global, payload);
});

addActionHandler('updatePerformanceSettings', (global, actions, payload): ActionReturnType => {
  global = {
    ...global,
    settings: {
      ...global.settings,
      performance: {
        ...global.settings.performance,
        ...payload,
      },
    },
  };

  return global;
});

addActionHandler('setThemeSettings', (global, actions, payload): ActionReturnType => {
  const { theme, ...rawSettings } = payload;
  const settings = normalizeThemeSettingsPatch(rawSettings);
  const previousThemeSettings = global.settings.themes[theme];

  if (DEBUG) {
    logDebugMessage('debug', '[ThemeSettingsAction] setThemeSettings:payload', {
      theme,
      currentTheme: global.settings.byKey.theme,
      cloudThemeId: global.settings.byKey.cloudThemeId,
      payload: buildThemeWallpaperDebugSnapshot(settings),
      previous: buildThemeWallpaperDebugSnapshot(previousThemeSettings),
    });
  }

  const updatedGlobal = replaceThemeSettings(global, theme, settings);

  if (DEBUG) {
    logDebugMessage('debug', '[ThemeSettingsAction] setThemeSettings:applied', {
      theme,
      next: buildThemeWallpaperDebugSnapshot(updatedGlobal.settings.themes[theme]),
    });
  }

  return updatedGlobal;
});

addActionHandler('requestNextSettingsScreen', (global, actions, payload): ActionReturnType => {
  const { screen, tabId = getCurrentTabId() } = payload;
  return updateTabState(global, {
    nextSettingsScreen: screen,
  }, tabId);
});

function normalizeThemeSettingsPatch(settings: Partial<IThemeSettings>) {
  // Only explicit wallpaper/background switches should auto-reset wallpaper-related fields.
  if (!('background' in settings)) {
    return settings;
  }

  return {
    ...settings,
    backgroundDocumentId: 'backgroundDocumentId' in settings ? settings.backgroundDocumentId : undefined,
    wallpaperSettings: 'wallpaperSettings' in settings ? settings.wallpaperSettings : undefined,
    isPatternWallpaper: 'isPatternWallpaper' in settings ? settings.isPatternWallpaper : undefined,
    isDarkWallpaper: 'isDarkWallpaper' in settings ? settings.isDarkWallpaper : undefined,
    wallpaperMimeType: 'wallpaperMimeType' in settings ? settings.wallpaperMimeType : undefined,
    isWallpaperOverridden: 'isWallpaperOverridden' in settings ? settings.isWallpaperOverridden : true,
  };
}
